import { FC, useMemo } from 'react';
import cx from 'classnames';

import { SvgIcon } from '@/components/SvgIcon';
import { getRiskTypeByValue, isFlowLite } from '@/helpers';
import { AmlFlowEnum } from '@/api';
import loaderImg from '@/assets/images/circle-preloader.svg';

import styles from './RiskIndicator.module.sass';
import { useTranslation } from 'react-i18next';

interface RiskIndicatorProps {
  value: number;
  isChecking?: boolean;
  className?: string;
  amlFlow?: AmlFlowEnum;
}

const RiskIndicator: FC<RiskIndicatorProps> = ({
  value,
  isChecking,
  className,
  amlFlow,
}) => {
  const { t } = useTranslation();

  const riskType = useMemo(() => getRiskTypeByValue(value), [value]);
  const degrees = useMemo(() => {
    return Math.round((value / 100) * 180);
  }, [value]);

  const classes = cx(
    styles.risk,
    styles[`risk--${riskType}`],
    {
      [styles['is-checking']]: isChecking,
    },
    className,
  );

  const from = useMemo(() => (isFlowLite(amlFlow) ? 'Low' : '0'), [amlFlow]);
  const to = useMemo(() => (isFlowLite(amlFlow) ? 'High' : '100'), [amlFlow]);

  return (
    <div className={classes}>
      <div className={styles.inner}>
        <div className={styles.gray}>
          {!isChecking && (
            <div
              className={styles.level}
              style={{ transform: `rotate(${degrees}deg)` }}
            >
              <div className={styles['level-fill']} />
            </div>
          )}
        </div>
      </div>

      <div
        className={styles['level-white']}
        style={{ transform: `rotate(${degrees}deg)` }}
      >
        {!isChecking && (
          <div className={styles['level-arrow']}>
            <div className={styles['level-arrow-in']} />
          </div>
        )}
      </div>

      {isChecking && (
        <>
          <img src={loaderImg} alt="" className={styles.loader} />
          <div className={styles.checking}>
            {t('translation.risk_checking')}
          </div>
        </>
      )}

      {!isChecking && (
        <div className={styles.value}>
          <SvgIcon name="risk" className={styles.value__icon} />
          <span className={styles.value__number}>{value}%</span>
        </div>
      )}

      <div className={styles.from}>{from}</div>
      <div className={styles.to}>{to}</div>
    </div>
  );
};

export default RiskIndicator;
